import Settings from '../domain/Settings';
import Slot from '../domain/Slot';
import {getPageTargeting, getSlotTargeting} from '../services/targeting';
import * as perf from '../utils/performance';
import {AdServer} from '../domain/AdServer';
import {getDMOI} from '../services/userService';
import {hasPurposesConsent} from '../services/privacyService';
import {getCurrentBreakpoint} from '../services/breakpoints';
import {isObject} from '../utils/object-util';
import {getForcedCreativeId, getPlacementOverride} from '../utils/debug-overrides';

declare global {
	interface Window {
		ootag?: OptOut.OOTag;
	}
}

const DEFAULT_INIT_TIMEOUT = 1000;

let slotsDefined: Record<Slot['name'], boolean> = {},
	enabled = false;

window.ootag = window.ootag || {};
window.ootag.queue = window.ootag.queue || [];

const queueReady = new Promise<void>((resolve) => {
	window.ootag.queue.push(() => {
		resolve();
	});
});

export async function init(): Promise<void> {
	perf.mark('optout - init - start');

	try {
		const settings = Settings.getInstance();

		slotsDefined = {};
		resetOoTag();

		enabled = false;

		if (!settings.features?.optOut)
			return;

		if (!settings.optOut?.publisher || !settings.optOut?.slots) {
			console.warn('[ADVERT] OptOut module is loaded but is missing configuration.');
			return;
		}

		if (settings.optOut.onlyNoConsent && hasPurposesConsent([1])) {
			return;
		}

		perf.mark('optout - ootag.queue wait - start');
		try {
			await Promise.race([
				queueReady,
				new Promise((_, reject) => {
					setTimeout(() => reject(), settings.optOut.initTimeout ?? DEFAULT_INIT_TIMEOUT);
				})
			]);
		} catch {
			console.error('[ADVERT] OptOut module timed out waiting for ootag.queue to be ready.');
			perf.mark('optout - ootag.queue wait - timeout');
			return;
		} finally {
			perf.mark('optout - ootag.queue wait - end');
		}

		enabled = true;

		initializeOOTag();
	} finally {
		perf.mark('optout - init - end');
	}
}

function initializeOOTag() {
	const {publisher, onlyNoConsent, hideOutstreamControls} = Settings.getInstance().optOut;

	window.ootag.initializeOo({
		publisher,
		onlyNoConsent,
		noRequestsOnPageLoad: true,
		consentTimeOutMS: 500,
		hideOutstreamControls: hideOutstreamControls ?? true,
		// this is required, otherwise the showAd hijack doesn't work
		// because ootag applies an observer before calling it, which can't find the slot
		lazyLoading: {
			disabled: true
		},
	});

	addUserId();

	addTargeting();
}

function addUserId() {
	const userId = getDMOI();

	if (userId) {
		window.ootag.addParameter('ppid', userId);
	}
}

function resetOoTag() {
	if (!enabled) {
		return;
	}

	window.ootag.undefineAllSlots();
	// Not ideal to interfere directly, but no way around it right now
	window.ootag.customParams = {};
	window.ootag.customParamsPerSlot = {};
}

function addTargeting() {
	const {includeTargeting, pageTargetingKeys} = Settings.getInstance().optOut;

	if (!includeTargeting) {
		return;
	}

	const pageTargeting = getPageTargeting();

	Object.entries(pageTargeting)
		.filter(([key]) => pageTargetingKeys?.includes(key) ?? true)
		.forEach(([key, value]) => {
			window.ootag.addParameter(
				key,
				getSafeTargetingValue(value)
			);
		});
}

function addSlotTargeting(slot: Slot) {
	const {includeTargeting, slotTargetingKeys} = Settings.getInstance().optOut;

	if (!includeTargeting) {
		return;
	}

	const slotTargeting = getSlotTargeting(slot);

	Object.entries(slotTargeting)
		.filter(([key]) => slotTargetingKeys?.includes(key) ?? true)
		.forEach(([key, value]) => {
			window.ootag.addParameterForSlot(
				getAdSlotName(slot),
				key,
				getSafeTargetingValue(value)
			);
		});
}

function getAdSlotName(slot: Slot): string {
	const adSlotName = getPlacementOverride(slot.name, AdServer.OptOut) ?? Settings.getInstance().optOut.slots[slot.name];

	if (isObject(adSlotName)) {
		const currentBp = getCurrentBreakpoint();

		return adSlotName[currentBp];
	}

	return adSlotName;
}

function getSafeTargetingValue(value: unknown): string {
	return Array.isArray(value) ? value.join(',') : String(value);
}

export function defineSlot(slot: Slot): boolean {
	const adSlot = getAdSlotName(slot);

	if (!adSlot || slotsDefined[slot.name])
		return false;

	slotsDefined[slot.name] = true;

	window.ootag.defineSlot({
		adSlot,
		targetId: slot.domId,
		emptyCallback: () => onReceived(slot, null),
		filledCallback: (_, adResponse) => onReceived(slot, adResponse),
		adShownCallback: () => onRendered(slot),
	});

	addSlotTargeting(slot);
	slot.setDefined(true, AdServer.OptOut);

	const previewId = getForcedCreativeId(slot.name, AdServer.OptOut);

	if (previewId) {
		window.ootag.previewAdSlots ??= {};
		window.ootag.previewAdSlots[adSlot] = previewId;
	}

	return true;
}

export async function loadSlots(slots: Slot[]) {
	const availableSlots = slots
		.map(s => ({slot: s, adSlot: getOOtagSlot(s)}))
		.filter(s => !!s.adSlot);

	if (!availableSlots.length) {
		return;
	}

	const optOutResponsePromise = window.ootag.getResponsesNoRendering(
		Settings.getInstance().optOut.publisher,
		availableSlots.map(s => s.adSlot),
		null,
		'prebid'
	);

	availableSlots.forEach(({slot}) => {
		slot.setRequested(true, AdServer.OptOut);
	});

	try {
		const responseMap = await optOutResponsePromise;

		markAsReceived(responseMap, availableSlots);
	} catch (error) {
		console.error('[ADVERT] Failed to get responses from Opt Out', error);
	}

}

function markAsReceived(responseMap: Map<string, OptOut.AdObject>, availableSlots: {
	slot: Slot,
	adSlot: OptOut.OOTagAdSlot
}[]) {
	for (const [id, response] of responseMap) {
		const slot = availableSlots.find((s) => s.adSlot.adSlot === id);

		if (!slot) {
			console.error(`[ADVERT] Could not find OptOut slot ${id} in available slots`);
			continue;
		}

		onReceived(slot.slot, response);
	}
}

function getOOtagSlot(slot: Slot): OptOut.OOTagAdSlot | null {
	const adSlotName = getAdSlotName(slot);

	return window.ootag.slots.find(s => s.adSlot === adSlotName) ?? null;
}


function onReceived(slot: Slot, adResponse: OptOut.AdObject | null) {
	slot.setAdData(AdServer.OptOut, adResponse);
	slot.setReceived(true, AdServer.OptOut);
}

function onRendered(slot: Slot) {
	slot.setRendered(true, AdServer.OptOut);
}

export function renderSlot(slot: Slot): void {
	window.ootag.renderNoLazyloading(slot.domId, slot.adResponses[AdServer.OptOut].requestId, slot.adResponses[AdServer.OptOut]);

	slot.setShowTagCalled(true, AdServer.OptOut);
}

export function resetSlot(slot: Slot): void {
	const adSlot = getAdSlotName(slot);

	if (!window.ootag.customParamsPerSlot?.[adSlot])
		return;

	window.ootag.customParamsPerSlot[adSlot] = {};
	addSlotTargeting(slot);
}

export function refreshSlots(slots: Slot[]): void {
	slots
		.filter(s => slotsDefined[s.name])
		.forEach(s => refreshSlot(s));
}

function refreshSlot(slot: Slot) {
	window.ootag.refreshSlot(getAdSlotName(slot));
}

export function updatePageTargeting(): void {
	window.ootag.customParams = {};
	addTargeting();
}

export function isEnabled(): boolean {
	return enabled;
}

export function resizeSlot(slot: Slot, newSize: [number, number]): void {
	const ootagSlot = window.ootag.slots?.find(s => s.targetId === slot.domId);

	ootagSlot.lastResponse.width = newSize[0];
	ootagSlot.lastResponse.height = newSize[1];
}

// Unsupported
//  Can't set Prebid user data
//      setUserPageOptions(userIds: AnyObject[]): void
//  Can't manage sizes
//      applySizeMapping(slot: Slot, sizeMapping: SizeMapping): void
//  No additional removal needed, resetOOtag covers it
//      removeSlot(slot: Slot): void
